/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6200-G2 Platform Configuration
 * Defines MA6200-G2-specific behavior for topology display and interaction
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { PlatformConfig } from "./PlatformConfig";
import { ma6200g2HierarchyBuilder } from "../builders/MA6200G2HierarchyBuilder";

export class MA6200G2PlatformConfig implements PlatformConfig {
  platformId = "ma6200g2";
  displayName = "MA6200-G2";
  hierarchyBuilder = ma6200g2HierarchyBuilder;

  matches(platformId: string): boolean {
    const normalized = platformId?.toLowerCase().replace(/[-_\s]/g, "") || "";
    return normalized.includes("ma6200g2");
  }

  findDeviceForNode(
    nodeId: string,
    node: IInventoryGraphNode | undefined,
    listData: IRemoteEquipment[],
    rootId: string
  ): IRemoteEquipment | null {
    const isRootRoute = (route?: string) => {
      const r = (route || "").trim();
      return r === "" || r === "0" || r === "0.0.0.0";
    };

    // Root node in MA6200-G2 topology should always open the A3 drawer.
    if (nodeId === rootId || node?.type === "site") {
      const primaryA3 = listData.find((d) => {
        const route = d.topology || "";
        const equipmentType = d.equipmentType?.toUpperCase() || "";
        return isRootRoute(route) && equipmentType.includes("A3");
      });

      if (primaryA3) {
        console.log("[MA6200-G2] Found root A3 device:", primaryA3.objectKey, primaryA3.equipmentType);
        return primaryA3;
      }
    }

    const device = listData.find((d) => d.objectKey === nodeId || d.managedObject === nodeId);
    if (device) {
      console.log("[MA6200-G2] Found device:", device.objectKey, device.equipmentType);
      return device;
    }

    if (node) {
      let fallbackDevice = listData.find((d) => d.serialNumber === node.label);
      if (fallbackDevice) {
        console.log("[MA6200-G2] Found device by serialNumber (FALLBACK):", fallbackDevice.serialNumber);
        return fallbackDevice;
      }

      if (node.ipAddress) {
        fallbackDevice = listData.find((d) => d.ipAddress === node.ipAddress);
        if (fallbackDevice) {
          console.log("[MA6200-G2] Found device by ipAddress (FALLBACK):", fallbackDevice.ipAddress);
          return fallbackDevice;
        }
      }
    }

    console.warn("[MA6200-G2] No device found for node:", nodeId, node);
    return null;
  }

  getRootLedColor(listData: IRemoteEquipment[]): string {
    const isRootRoute = (route?: string) => {
      const r = (route || "").trim();
      return r === "" || r === "0" || r === "0.0.0.0";
    };

    // Find primary A3 (root) at route 0.0.0.0
    const primaryA3 = listData.find((d) => {
      const route = d.topology || "";
      const equipmentType = d.equipmentType?.toUpperCase() || "";
      return isRootRoute(route) && equipmentType.includes("A3");
    });

    if (!primaryA3) return "#9e9e9e";

    const status = primaryA3.status?.toString().toLowerCase() || "";
    if (status === "connected" || status === "online") return "#4caf50";
    if (status === "disconnected" || status === "offline") return "#f44336";
    if (status === "warning" || status === "minor") return "#ff9800";
    if (status === "critical" || status === "major") return "#f44336";
    return "#9e9e9e";
  }

  metadata = {
    rootEquipmentType: "A3",
    primaryA3Criteria: {
      route: "0.0.0.0",
    },
  };
}
